import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';

/**
 * Returns the list of bookable Client Director CalendarSessions linked to the
 * project. Both one-on-one and round-robin sessions are included by design; only
 * active sessions whose consultant has a connected calendar are surfaced —
 * otherwise availability can't return any slots. Merges the many_many
 * `ClientDirectorCalendarSessions` with the legacy has_one
 * `ClientDirectorCalendarSession` for backward compatibility.
 * `canBookDirect` is true when at least one bookable session exists.
 */
export const useProjectBookMeeting = () => {
    const { project } = useLoaderData<typeof loader>();

    const manySessions = (project.ClientDirectorCalendarSessions ?? []).filter(
        (s): s is NonNullable<typeof s> =>
            s !== null && s.IsActive && s.HasConnectedConsultant,
    );

    // Fallback: honour the legacy has_one if the many_many list is empty.
    const legacySession = project.ClientDirectorCalendarSession;
    const legacyValid =
        Boolean(
            legacySession?.IsActive && legacySession.HasConnectedConsultant,
        ) && !manySessions.some((s) => s.ID === legacySession?.ID);

    const sessions =
        legacyValid && legacySession
            ? [...manySessions, legacySession]
            : manySessions;

    const canBookDirect = sessions.length > 0;

    return { canBookDirect, sessions };
};
